import React, { useState, useRef } from "react";
import env from "@beam-australia/react-env";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";

import { useTranslation } from "react-i18next";

import useApi from "../../../hooks/useApi";
import useSkeleton from "../../../hooks/useSkeleton";

import Layout from "../../../layout/layout";
import Header from "../../../layout/header";

import helperFormats from "../../../utils/helperFormats";

// ---------------------------------------------------------------------------
// CSV builder helpers
// ---------------------------------------------------------------------------

const roundAmount = (value) => Number(value ?? 0);

const escapeCsvValue = (value, { decimal, thousand, isNumber }) => {
    if (value == null) return '""';
    let str = String(value);
    if (isNumber && str !== "" && !isNaN(Number(str))) {
        let num = Number(str);
        str = num
            .toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
            .replace(/\./g, "__DOT__")
            .replace(/,/g, "__COMMA__");
        str = str
            .replace(/__DOT__/g, decimal)
            .replace(/__COMMA__/g, thousand);
    }
    const escapedValue = str.replace(/"/g, '""');
    return `"${escapedValue}"`;
};

const getInstallmentComparableDate = (installment) =>
    new Date(installment?.expirationDate ?? installment?.installmentDate ?? 0).getTime();

const getInstallmentPeriod = (installment) => {
    const rawDate = installment?.expirationDate ?? installment?.installmentDate;
    if (!rawDate) return null;
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return null;
    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        label: helperFormats.formatDate(date),
    };
};

const getActiveCreditsCount = (rowData) => {
    const installments = rowData.installmentsCollection ?? [];
    const activeCredits = new Set();
    installments.forEach((installment) => {
        const credit = installment.credit;
        const creditId = Number(credit?.id ?? installment.creditId ?? 0);
        if (!creditId) return;
        const closedDate = credit?.closedDate ? new Date(credit.closedDate) : null;
        const isActive =
            !closedDate ||
            Number.isNaN(closedDate.getTime()) ||
            closedDate.getFullYear() <= 1;
        if (isActive) {
            activeCredits.add(creditId);
        }
    });
    return activeCredits.size;
};

const hasWithholdingLineAmountsMismatch = (rowData) => {
    const a = roundAmount(rowData.amountSent);
    const b = roundAmount(rowData.amountConfirmed);
    const c = roundAmount(rowData.amountConcilied);
    return !(Math.abs(a - b) < 0.01 && Math.abs(b - c) < 0.01);
};

const buildInstallmentExportRows = (rowData) => {
    const amountConfirmed = roundAmount(rowData.amountConfirmed);
    const totalCurrentAccountApplied = roundAmount(rowData.currentAccountApplied);

    const baseRow = {
        withholdingYear: rowData.withholdingYear ?? "",
        withholdingMonth: rowData.withholdingMonth ?? "",
        withholdingsLineId: rowData.id,
        beneficiaryDocumentNumber: rowData.beneficiary_DocumentNumber ?? "",
        beneficiaryName: rowData.beneficiary_Name ?? "",
        beneficiaryLastName: rowData.beneficiary_LastName ?? "",
        creditsActive: getActiveCreditsCount(rowData),
        amountGenerated: roundAmount(rowData.amountGenerated),
        amountSent: roundAmount(rowData.amountSent),
        amountConfirmed,
        amountConcilied: roundAmount(rowData.amountConcilied),
        currentAccountApplied: totalCurrentAccountApplied,
        hasMismatch: hasWithholdingLineAmountsMismatch(rowData) ? "SI" : "NO",
    };

    const withholdingPayments = rowData.withholdingPayments ?? [];
    const installmentBuckets = new Map();

    const ensureBucket = (installment) => {
        if (!installment) return null;
        const installmentId = Number(installment.id);
        if (!installmentBuckets.has(installmentId)) {
            installmentBuckets.set(installmentId, {
                installment,
                installmentCapital: 0,
                installmentInterests: 0,
                installmentInterestsTaxes: 0,
                arrearsCapital: 0,
                arrearsInterests: 0,
                arrearsInterestsTaxes: 0,
                arrearsTaxes: 0,
                arrearsDays: 0,
                arrearsSeen: new Set(),
            });
        }
        return installmentBuckets.get(installmentId);
    };

    withholdingPayments.forEach((payment) => {
        (payment.installmentsPaymentsCollection ?? []).forEach((ip) => {
            const bucket = ensureBucket(ip.installment);
            if (!bucket) return;
            bucket.installmentCapital += roundAmount(ip.capital);
            bucket.installmentInterests += roundAmount(ip.interests);
            bucket.installmentInterestsTaxes += roundAmount(ip.interestsTaxes);
        });

        (payment.installmentsArrearsPaymentsCollection ?? []).forEach((iap) => {
            const arrear = iap.installmentArrear;
            if (!arrear) return;
            const bucket = ensureBucket(arrear.installment);
            if (!bucket) return;
            bucket.arrearsCapital += roundAmount(iap.capital);
            bucket.arrearsInterests += roundAmount(iap.interests);
            bucket.arrearsInterestsTaxes += roundAmount(iap.interestsTaxes);
            bucket.arrearsTaxes += roundAmount(iap.arrearTaxes);
            const arrearId = Number(arrear.id);
            if (!bucket.arrearsSeen.has(arrearId)) {
                bucket.arrearsSeen.add(arrearId);
                bucket.arrearsDays += Number(arrear.days ?? 0);
            }
        });
    });

    if (installmentBuckets.size === 0) {
        const fallbackInstallments = rowData.installmentsCollection ?? [];
        const fallbackCreditCodes = [
            ...new Set(fallbackInstallments.map((i) => i.credit?.code).filter(Boolean)),
        ];
        const fallbackCreditCode = fallbackCreditCodes.join(", ");

        return [
            {
                ...baseRow,
                creditCode: fallbackCreditCode,
                installmentNumber: 0,
                installmentTerm: 0,
                installmentDueDate: "",
                installmentTotal: 0,
                currentAccountInstallmentApplied: 0,
                arrearsDays: 0,
                arrearsCapital: 0,
                arrearsInterests: 0,
                arrearsInterestsTaxes: 0,
                arrearsTaxes: 0,
                arrearsTotal: 0,
                confirmedInstallmentCapital: 0,
                confirmedInstallmentInterests: 0,
                confirmedInstallmentTaxes: 0,
                confirmedArrearCapital: 0,
                confirmedArrearInterests: 0,
                confirmedArrearInterestsTaxes: 0,
                confirmedArrearTaxes: 0,
                confirmedPaymentsTotal: 0,
                confirmedPaymentsDifference: amountConfirmed,
                confirmedPaymentsControl: amountConfirmed === 0 ? "OK" : "NO CUADRA",
            },
        ];
    }

    const buckets = Array.from(installmentBuckets.values()).sort(
        (a, b) =>
            getInstallmentComparableDate(a.installment) -
            getInstallmentComparableDate(b.installment)
    );

    const totalInstallmentConfirmedBase = buckets.reduce(
        (total, b) =>
            total +
            roundAmount(b.installmentCapital) +
            roundAmount(b.installmentInterests) +
            roundAmount(b.installmentInterestsTaxes),
        0
    );

    return buckets.map((bucket) => {
        const { installment } = bucket;
        const period = getInstallmentPeriod(installment);
        const installmentNumber = Number(installment.number ?? 0);
        const creditInstallmentsNumber = Number(
            installment.credit?.installmentsNumber ?? 0
        );

        const confirmedInstallmentBase = roundAmount(
            bucket.installmentCapital +
            bucket.installmentInterests +
            bucket.installmentInterestsTaxes
        );

        const currentAccountInstallmentApplied =
            totalInstallmentConfirmedBase > 0
                ? roundAmount(
                    (confirmedInstallmentBase / totalInstallmentConfirmedBase) *
                    totalCurrentAccountApplied
                )
                : 0;

        const arrearsTotal = roundAmount(
            bucket.arrearsCapital +
            bucket.arrearsInterests +
            bucket.arrearsInterestsTaxes +
            bucket.arrearsTaxes
        );

        const confirmedPaymentsTotal = roundAmount(
            confirmedInstallmentBase + arrearsTotal
        );
        const confirmedPaymentsDifference = roundAmount(
            amountConfirmed - confirmedPaymentsTotal
        );

        return {
            ...baseRow,
            creditCode: installment.credit?.code ?? "",
            installmentNumber,
            installmentTerm: creditInstallmentsNumber,
            installmentDueDate: period?.label ?? "",
            installmentTotal: confirmedInstallmentBase,
            currentAccountInstallmentApplied,
            arrearsDays: bucket.arrearsDays,
            arrearsCapital: roundAmount(bucket.arrearsCapital),
            arrearsInterests: roundAmount(bucket.arrearsInterests),
            arrearsInterestsTaxes: roundAmount(bucket.arrearsInterestsTaxes),
            arrearsTaxes: roundAmount(bucket.arrearsTaxes),
            arrearsTotal,
            confirmedInstallmentCapital: roundAmount(bucket.installmentCapital),
            confirmedInstallmentInterests: roundAmount(bucket.installmentInterests),
            confirmedInstallmentTaxes: roundAmount(bucket.installmentInterestsTaxes),
            confirmedArrearCapital: roundAmount(bucket.arrearsCapital),
            confirmedArrearInterests: roundAmount(bucket.arrearsInterests),
            confirmedArrearInterestsTaxes: roundAmount(bucket.arrearsInterestsTaxes),
            confirmedArrearTaxes: roundAmount(bucket.arrearsTaxes),
            confirmedPaymentsTotal,
            confirmedPaymentsDifference,
            confirmedPaymentsControl:
                Math.abs(confirmedPaymentsDifference) < 0.01 ? "OK" : "NO CUADRA",
        };
    });
};

const sortDetailRows = (rows) =>
    rows.sort((a, b) => {
        const ay = Number(a.withholdingYear ?? 0);
        const by = Number(b.withholdingYear ?? 0);
        if (ay !== by) return ay - by;
        const am = Number(a.withholdingMonth ?? 0);
        const bm = Number(b.withholdingMonth ?? 0);
        if (am !== bm) return am - bm;
        const ad = `${a.beneficiaryDocumentNumber ?? ""}`;
        const bd = `${b.beneficiaryDocumentNumber ?? ""}`;
        if (ad !== bd) return ad.localeCompare(bd, undefined, { numeric: true });
        const ac = `${a.creditCode ?? ""}`;
        const bc = `${b.creditCode ?? ""}`;
        if (ac !== bc) return ac.localeCompare(bc, undefined, { numeric: true });
        return Number(a.installmentNumber ?? 0) - Number(b.installmentNumber ?? 0);
    });

// decimal y thousand determinan el formato numérico; delimiter es el separador de columnas.
// Los textos (encabezados) son siempre en español.
const buildCsvBlob = (lines, { decimal, thousand, delimiter }) => {
    const headers = [
        "Año", "Mes", "Documento", "Nombre", "Apellido", "CreditosActivos",
        "MontoGenerado", "MontoEnviado", "MontoConfirmado", "MontoConciliado",
        "CuentaCorrienteAplicada", "Inconsistencia", "CodigoCredito", "Cuota",
        "Plazo", "Vencimiento", "CuotaCapital", "CuotaIntereses", "CuotaImpuestos", "CuentaCorrienteCuota",
        "MoraDias", "MoraCapital", "MoraIntereses", "MoraImpuestosIntereses",
        "MoraImpuestosMora", "MoraTotal", "PagosConfirmadosCuotaCapital",
        "PagosConfirmadosCuotaIntereses", "PagosConfirmadosCuotaImpuestos",
        "PagosConfirmadosMoraCapital", "PagosConfirmadosMoraIntereses",
        "PagosConfirmadosMoraImpuestosIntereses", "PagosConfirmadosMoraImpuestosMora",
        "TotalPagosConfirmados",
    ];

    const detailRows = lines.flatMap((line) => buildInstallmentExportRows(line));

    const csvRowValues = (row) => [
        row.withholdingYear,
        row.withholdingMonth,
        row.beneficiaryDocumentNumber,
        row.beneficiaryName,
        row.beneficiaryLastName,
        row.creditsActive,
        row.amountGenerated,
        row.amountSent,
        row.amountConfirmed,
        row.amountConcilied,
        row.currentAccountApplied,
        row.hasMismatch,
        row.creditCode,
        row.installmentNumber,
        row.installmentTerm,
        row.installmentDueDate,
        row.confirmedInstallmentCapital,
        row.confirmedInstallmentInterests,
        row.confirmedInstallmentTaxes,
        row.currentAccountInstallmentApplied,
        row.arrearsDays,
        row.arrearsCapital,
        row.arrearsInterests,
        row.arrearsInterestsTaxes,
        row.arrearsTaxes,
        row.arrearsTotal,
        row.confirmedInstallmentCapital,
        row.confirmedInstallmentInterests,
        row.confirmedInstallmentTaxes,
        row.confirmedArrearCapital,
        row.confirmedArrearInterests,
        row.confirmedArrearInterestsTaxes,
        row.confirmedArrearTaxes,
        row.confirmedPaymentsTotal,
    ];

    sortDetailRows(detailRows);

    const fmt = { decimal, thousand, isNumber: false };
    const csvLines = [
        headers.map((h) => escapeCsvValue(h, fmt)).join(delimiter),
        ...detailRows.map((row) =>
            csvRowValues(row)
                .map((v) => escapeCsvValue(v, { decimal, thousand, isNumber: typeof v === "number" && Number.isFinite(v) }))
                .join(delimiter)
        ),
    ];

    const csvContent = `﻿${csvLines.join("\n")}`;
    return new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const WithholdingsByPeriodReport = () => {
    const { t } = useTranslation();
    const api = useApi();
    const s = useSkeleton("commons");
    const toast = useRef(null);
    const dt = useRef(null);

    const today = new Date();
    const [fromYear, setFromYear] = useState(today.getFullYear());
    const [fromMonth, setFromMonth] = useState(today.getMonth() + 1);
    const [toYear, setToYear] = useState(today.getFullYear());
    const [toMonth, setToMonth] = useState(today.getMonth() + 1);

    const [isLoading, setIsLoading] = useState(false);
    const [rawLines, setRawLines] = useState(null);
    const [reportRows, setReportRows] = useState(null);
    const [globalFilter, setGlobalFilter] = useState("");

    // Estado del diálogo de exportación
    const [exportDialogVisible, setExportDialogVisible] = useState(false);
    const [exportNumFormat, setExportNumFormat] = useState("es"); // "es" | "en"
    const [exportDelimiter, setExportDelimiter] = useState(";");  // ";" | ","

    const fmtNum = (value) =>
        value == null || value === ""
            ? ""
            : Number(value).toLocaleString("de-UY", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });

    const showError = (detail) => {
        toast.current?.show({
            severity: "error",
            summary: t("forms.messages.titles.error"),
            detail,
            life: 5000,
        });
    };

    const validateRange = () => {
        if (!fromYear || !toYear || !fromMonth || !toMonth) {
            showError("Complete año/mes desde y hasta.");
            return false;
        }
        if (fromMonth < 1 || fromMonth > 12 || toMonth < 1 || toMonth > 12) {
            showError("Mes inválido.");
            return false;
        }
        if (fromYear * 12 + fromMonth > toYear * 12 + toMonth) {
            showError("El período 'desde' debe ser menor o igual al 'hasta'.");
            return false;
        }
        return true;
    };

    const loadReport = async () => {
        if (!validateRange()) return;
        setIsLoading(true);
        try {
            const response = await api.get(
                `${env("API_URL")}/withholdings/withholdingsLinesByPeriod?fromYear=${fromYear}&fromMonth=${fromMonth}&toYear=${toYear}&toMonth=${toMonth}`
            );
            const lines = response.data ?? [];

            if (lines.length === 0) {
                toast.current?.show({
                    severity: "info",
                    summary: t("forms.messages.titles.suggestion"),
                    detail: "No se encontraron retenciones para el rango seleccionado.",
                    life: 4000,
                });
                setRawLines([]);
                setReportRows([]);
                return;
            }

            const rows = lines.flatMap(buildInstallmentExportRows);
            rows.forEach((row, idx) => {
                row._rowId = `${row.withholdingsLineId}_${row.installmentNumber}_${idx}`;
            });
            sortDetailRows(rows);
            setRawLines(lines);
            setReportRows(rows);
            setGlobalFilter("");
        } catch (error) {
            console.error(error);
            showError(t("forms.messages.operation.error"));
        } finally {
            setIsLoading(false);
        }
    };

    const openExportDialog = () => {
        if (!rawLines || rawLines.length === 0) {
            showError("Primero consulte el reporte.");
            return;
        }
        setExportDialogVisible(true);
    };

    const doExportCsv = () => {
        const numConfig = exportNumFormat === "en"
            ? { decimal: ".", thousand: "," }
            : { decimal: ",", thousand: "." };

        const blob = buildCsvBlob(rawLines, { ...numConfig, delimiter: exportDelimiter });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `retenciones-${fromYear}-${String(fromMonth).padStart(2, "0")}_a_${toYear}-${String(toMonth).padStart(2, "0")}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
        setExportDialogVisible(false);
        toast.current?.show({
            severity: "success",
            summary: t("forms.messages.titles.success"),
            detail: `${rawLines.length} líneas exportadas.`,
            life: 4000,
        });
    };

    const exportDialogFooter = (
        <div>
            <Button
                label="Cancelar"
                icon="pi pi-times"
                outlined
                onClick={() => setExportDialogVisible(false)}
            />
            <Button
                label="Exportar"
                icon="pi pi-download"
                onClick={doExportCsv}
            />
        </div>
    );

    const tableHeader = (
        <div className="table-header">
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                    type="search"
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={`${t("DataTable.Search")}...`}
                />
            </span>
            <Button
                outlined
                label="Exportar CSV"
                icon="pi pi-upload"
                className="p-button-secondary"
                onClick={openExportDialog}
                style={{ marginLeft: "20px" }}
            />
        </div>
    );

    const controlBody = (row) => (
        <span style={{ color: row.confirmedPaymentsControl === "OK" ? "green" : "red", fontWeight: "bold" }}>
            {row.confirmedPaymentsControl}
        </span>
    );

    const mismatchBody = (row) => (
        <span style={{ color: row.hasMismatch === "NO" ? "green" : "red", fontWeight: "bold" }}>
            {row.hasMismatch}
        </span>
    );

    return (
        <Layout
            header={
                <Header
                    icon={<i className="pi pi-file-export"></i>}
                    title={t("menu.reportWithholdingsByPeriod")}
                />
            }
        >
            <Toast ref={toast} />

            <Dialog
                header="Opciones de exportación"
                visible={exportDialogVisible}
                style={{ width: 380 }}
                footer={exportDialogFooter}
                onHide={() => setExportDialogVisible(false)}
            >
                <div className="mb-4">
                    <p className="mt-0 mb-2 font-semibold">Formato de números</p>
                    <div className="flex flex-column gap-2">
                        <label className="flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                            <input
                                type="radio"
                                name="exportNumFormat"
                                value="es"
                                checked={exportNumFormat === "es"}
                                onChange={() => setExportNumFormat("es")}
                            />
                            <span>Español &nbsp;<span style={{ color: "#888", fontSize: "0.85em" }}>1.234,56 &nbsp;·&nbsp; separador: punto</span></span>
                        </label>
                        <label className="flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                            <input
                                type="radio"
                                name="exportNumFormat"
                                value="en"
                                checked={exportNumFormat === "en"}
                                onChange={() => setExportNumFormat("en")}
                            />
                            <span>English &nbsp;<span style={{ color: "#888", fontSize: "0.85em" }}>1,234.56 &nbsp;·&nbsp; decimal: dot</span></span>
                        </label>
                    </div>
                </div>
                <div>
                    <p className="mt-0 mb-2 font-semibold">Separador de columnas</p>
                    <div className="flex flex-column gap-2">
                        <label className="flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                            <input
                                type="radio"
                                name="exportDelimiter"
                                value=";"
                                checked={exportDelimiter === ";"}
                                onChange={() => setExportDelimiter(";")}
                            />
                            <span>Punto y coma &nbsp;<span style={{ color: "#888", fontSize: "0.85em" }}>( ; ) — recomendado para Excel en español</span></span>
                        </label>
                        <label className="flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                            <input
                                type="radio"
                                name="exportDelimiter"
                                value=","
                                checked={exportDelimiter === ","}
                                onChange={() => setExportDelimiter(",")}
                            />
                            <span>Coma &nbsp;<span style={{ color: "#888", fontSize: "0.85em" }}>( , ) — recomendado para Excel en inglés</span></span>
                        </label>
                    </div>
                </div>
            </Dialog>

            <div className={s.get("dataTableContainer")}>
                <div className="card p-4 mb-3" style={{ maxWidth: 720 }}>
                    <div className="grid formgrid">
                        <div className="field col-12 md:col-6">
                            <label className="block mb-2">Desde - Año</label>
                            <InputNumber
                                value={fromYear}
                                onValueChange={(e) => setFromYear(e.value)}
                                min={2000}
                                max={2099}
                                useGrouping={false}
                                showButtons
                                className="w-full"
                            />
                        </div>
                        <div className="field col-12 md:col-6">
                            <label className="block mb-2">Desde - Mes</label>
                            <InputNumber
                                value={fromMonth}
                                onValueChange={(e) => setFromMonth(e.value)}
                                min={1}
                                max={12}
                                useGrouping={false}
                                showButtons
                                className="w-full"
                            />
                        </div>
                        <div className="field col-12 md:col-6">
                            <label className="block mb-2">Hasta - Año</label>
                            <InputNumber
                                value={toYear}
                                onValueChange={(e) => setToYear(e.value)}
                                min={2000}
                                max={2099}
                                useGrouping={false}
                                showButtons
                                className="w-full"
                            />
                        </div>
                        <div className="field col-12 md:col-6">
                            <label className="block mb-2">Hasta - Mes</label>
                            <InputNumber
                                value={toMonth}
                                onValueChange={(e) => setToMonth(e.value)}
                                min={1}
                                max={12}
                                useGrouping={false}
                                showButtons
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="flex justify-content-end">
                        <Button
                            icon="pi pi-search"
                            label={isLoading ? "Consultando..." : "Consultar"}
                            onClick={loadReport}
                            disabled={isLoading}
                        />
                    </div>
                </div>

                {reportRows !== null && (
                    <DataTable
                        ref={dt}
                        value={reportRows}
                        dataKey="_rowId"
                        paginator
                        rows={100}
                        rowsPerPageOptions={[25, 100, 500, 1000]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate={`${t("DataTable.currentPageReportTemplate")} retenciones`}
                        showGridlines
                        columnResizeMode="fit"
                        sortMode="single"
                        removableSort
                        globalFilter={globalFilter}
                        globalFilterFields={[
                            "beneficiaryDocumentNumber",
                            "beneficiaryName",
                            "beneficiaryLastName",
                            "creditCode",
                        ]}
                        header={tableHeader}
                        className="flex flex-column w-full"
                        emptyMessage={t("DataTable.emptyMessage")}
                        scrollable
                    >
                        <Column field="withholdingYear" header="Año" sortable bodyStyle={{ textAlign: "center" }} />
                        <Column field="withholdingMonth" header="Mes" sortable bodyStyle={{ textAlign: "center" }} />
                        <Column field="beneficiaryDocumentNumber" header="Documento" sortable bodyStyle={{ textAlign: "center" }} />
                        <Column field="beneficiaryName" header="Nombre" sortable />
                        <Column field="beneficiaryLastName" header="Apellido" sortable />
                        <Column field="creditCode" header="Cód. Crédito" sortable bodyStyle={{ textAlign: "center" }} />
                        <Column field="installmentNumber" header="# Cuota" sortable bodyStyle={{ textAlign: "center" }} />
                        <Column field="installmentTerm" header="Plazo" sortable bodyStyle={{ textAlign: "center" }} />
                        <Column field="installmentDueDate" header="Vencimiento" sortable bodyStyle={{ textAlign: "center" }} />
                        <Column field="amountGenerated" header="Mto. Generado" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.amountGenerated)} />
                        <Column field="amountSent" header="Mto. Enviado" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.amountSent)} />
                        <Column field="amountConfirmed" header="Mto. Confirmado" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.amountConfirmed)} />
                        <Column field="amountConcilied" header="Mto. Conciliado" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.amountConcilied)} />
                        <Column field="confirmedInstallmentCapital" header="Cuota Capital" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.confirmedInstallmentCapital)} />
                        <Column field="confirmedInstallmentInterests" header="Cuota Intereses" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.confirmedInstallmentInterests)} />
                        <Column field="confirmedInstallmentTaxes" header="Cuota Impuestos" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.confirmedInstallmentTaxes)} />
                        <Column field="arrearsDays" header="Días Mora" sortable bodyStyle={{ textAlign: "center" }} />
                        <Column field="arrearsTotal" header="Total Mora" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.arrearsTotal)} />
                        <Column field="confirmedPaymentsTotal" header="Total Confirmado" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.confirmedPaymentsTotal)} />
                        <Column field="confirmedPaymentsDifference" header="Diferencia" sortable bodyStyle={{ textAlign: "right" }} body={(row) => fmtNum(row.confirmedPaymentsDifference)} />
                        <Column field="confirmedPaymentsControl" header="Control" sortable bodyStyle={{ textAlign: "center" }} body={controlBody} />
                        <Column field="hasMismatch" header="Inconsistencia" sortable bodyStyle={{ textAlign: "center" }} body={mismatchBody} />
                    </DataTable>
                )}
            </div>
        </Layout>
    );
};

export default WithholdingsByPeriodReport;
